import {
  DownloadOutlined,
  FormOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  FileTextOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { Button, Card, List, Typography, Space, Tag, Tooltip, Select, Row, Col } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import { dateFormatter } from 'utils/formaters';

const { Text, Title } = Typography;
const { Option } = Select;

const orderStates = {
  odt_asignada: {
    text: 'Sin Iniciar',
    color: 'purple',
    icon: <ClockCircleOutlined />,
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-800',
  },
  odt_en_proceso: {
    text: 'En Proceso',
    color: 'orange',
    icon: <PlayCircleOutlined />,
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-800',
  },
  finalizada: {
    text: 'Finalizado',
    color: 'green',
    icon: <CheckCircleOutlined />,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
  },
};

const OrderList = (props) => {
  const { orders, isLoading, dowloadODT, onFilterChange } = props;

  const handleFilterChange = (value) => {
    if (onFilterChange) {
      onFilterChange(value);
    }
  };

  if (!orders || orders.length === 0) {
    return (
      <div>
        {/* Filtros */}
        <Card className="shadow-sm border-0 mb-6">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="flex items-center space-x-2">
                <FilterOutlined className="text-gray-500" />
                <Text strong className="text-gray-700">
                  Filtrar por estado:
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Todos los estados"
                allowClear
                style={{ width: '100%' }}
                onChange={handleFilterChange}
                className="rounded-lg"
              >
                <Option value="odt_asignada">
                  <Space>
                    <ClockCircleOutlined className="text-purple-500" />
                    Sin Iniciar
                  </Space>
                </Option>
                <Option value="odt_en_proceso">
                  <Space>
                    <PlayCircleOutlined className="text-orange-500" />
                    En Proceso
                  </Space>
                </Option>
                <Option value="finalizada">
                  <Space>
                    <CheckCircleOutlined className="text-green-500" />
                    Finalizado
                  </Space>
                </Option>
              </Select>
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm border-0">
          <div className="text-center py-12">
            <FileTextOutlined className="text-6xl text-gray-300 mb-4" />
            <Title level={4} className="text-gray-500 mb-2">
              No hay órdenes disponibles
            </Title>
            <Text type="secondary">No tienes órdenes de trabajo asignadas en este momento</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Filtros */}
      <Card className="shadow-sm border-0 mb-6">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="flex items-center space-x-2">
              <FilterOutlined className="text-gray-500" />
              <Text strong className="text-gray-700">
                Filtrar por estado:
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Todos los estados"
              allowClear
              style={{ width: '100%' }}
              onChange={handleFilterChange}
              className="rounded-lg"
            >
              <Option value="odt_asignada">
                <Space>
                  <ClockCircleOutlined className="text-purple-500" />
                  Sin Iniciar
                </Space>
              </Option>
              <Option value="odt_en_proceso">
                <Space>
                  <PlayCircleOutlined className="text-orange-500" />
                  En Proceso
                </Space>
              </Option>
              <Option value="finalizada">
                <Space>
                  <CheckCircleOutlined className="text-green-500" />
                  Finalizado
                </Space>
              </Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <List
        loading={isLoading}
        dataSource={orders}
        grid={{
          gutter: [24, 24],
          xs: 1,
          sm: 1,
          md: 2,
          lg: 2,
          xl: 3,
          xxl: 4,
        }}
        pagination={{
          align: 'center',
          position: 'bottom',
          pageSize: 12,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} órdenes`,
          pageSizeOptions: ['6', '12', '18', '24'],
        }}
        renderItem={(order) => {
          const state = orderStates[order.stage] || orderStates.odt_asignada;

          return (
            <List.Item key={order.id_order}>
              <Card
                className={`shadow-sm border-0 hover:shadow-lg transition-all duration-300 ${state.bgColor} ${state.borderColor}`}
                hoverable
                styles={{ body: { padding: '20px' } }}
              >
                {/* Header con estado */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded-lg ${state.bgColor}`}>
                      <UserOutlined className={`text-lg ${state.textColor}`} />
                    </div>
                    <div>
                      <Text strong className={`text-lg ${state.textColor}`}>
                        {order.name_client}
                      </Text>
                      <div className="flex items-center space-x-1 mt-1">
                        {state.icon}
                        <Text className={`text-sm ${state.textColor}`}>{state.text}</Text>
                      </div>
                    </div>
                  </div>
                  <Tag color={state.color} className="px-3 py-1 rounded-full font-medium">
                    N° {order.id_budget}
                  </Tag>
                </div>

                {/* Información de la orden */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3">
                    <EnvironmentOutlined className="text-gray-400 mt-1" />
                    <div>
                      <Text type="secondary" className="text-sm">
                        Dirección
                      </Text>
                      <div>
                        <Text className="text-gray-700">
                          {order.address}, {order.commune}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CalendarOutlined className="text-gray-400 mt-1" />
                    <div>
                      <Text type="secondary" className="text-sm">
                        Fecha de Asignación
                      </Text>
                      <div>
                        <Text className="text-gray-700">
                          {dateFormatter(order.insertion_date_order).long}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <Space>
                    <Tooltip title="Ver detalles de la orden">
                      <Link to={`${order.id_order}`}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<FormOutlined />}
                          className="rounded-lg"
                        >
                          Ver Orden
                        </Button>
                      </Link>
                    </Tooltip>

                    <Tooltip title="Descargar ODT">
                      <Button
                        size="small"
                        disabled={order.stage !== 'finalizada'}
                        icon={<DownloadOutlined />}
                        onClick={() => dowloadODT(order.id_budget)}
                        className="rounded-lg"
                      >
                        Descargar
                      </Button>
                    </Tooltip>
                  </Space>
                </div>
              </Card>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

export default OrderList;
